import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSlate } from 'slate-react';
import { Editor as SlateEditor, Transforms, Path, Text } from 'slate';
import { Spinner } from 'app/components';
import { SearchInput } from 'app/components/ui';
import { v4 as uuidv4 } from 'uuid';
import { defaultParagraph } from 'slate/extensions/paragraph';
import { useEditorReferencesQuery } from 'app/api/documents/useEditorData';
import { ReferenceDataValue } from 'entities/document';
import { ReferenceTableElement } from 'slate/types/slate';
import { TablePickerGrid } from './TablePickerGrid';

interface ImportTableProps {
  onClose: (...args: unknown[]) => void;
}

export const ImportTable = ({ onClose }: ImportTableProps) => {
  const { t } = useTranslation('table-import');
  const [search, setSearch] = useState('');
  const { references, isLoading } = useEditorReferencesQuery() as { references: ReferenceDataValue[]; isLoading: boolean };
  const editor = useSlate();

  const handleReferenceSelection = (reference: ReferenceDataValue) => {
    if (!reference.selectionContent) {
      return;
    }

    const slateTable: ReferenceTableElement = {
      type: 'reference-table',
      referenceDataSuperId: reference.superId,
      uuid: uuidv4(),
      children: [],
    };

    const point = SlateEditor.end(editor, editor.lastSelection);
    if (point) {
      const [pNode] = SlateEditor.first(editor, point);
      if (Text.isText(pNode) && !!pNode.text) {
        Transforms.insertNodes(editor, [slateTable, defaultParagraph()], { voids: true, at: point });
      } else {
        Transforms.setNodes(editor, slateTable as Partial<ReferenceTableElement>, { voids: true, at: point });
        const tablePath = point.path.slice(0, -1);
        Transforms.insertNodes(editor, defaultParagraph(), { at: Path.next(tablePath) });
      }
    }
    onClose();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div>
      <div className="header d-flex">
        <h3>{t('select-data')}</h3>
        <SearchInput className="table-picker-search" searchInput={search} setSearchInput={setSearch} placeholder={t('search-placeholder')} />
      </div>
      <div className="body">
        <TablePickerGrid references={references} search={search} onConfirm={handleReferenceSelection} />
      </div>
    </div>
  );
};
